import React from 'react';
import { Box, Container, Typography, Button, Paper } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import BlockIcon from '@mui/icons-material/Block';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Text, RichText, TextField } from '@sitecore-jss/sitecore-jss-nextjs';

export interface AccessDeniedFields {
  AccessDeniedTitle?: TextField;
  AccessDeniedSummary?: TextField;
  AccessDeniedInstructions?: { value: string };
  AccessDeniedCTAButton?: {
    value: {
      href?: string;
      text?: string;
    };
  };
  AccessDeniedHelpText?: TextField;
  /** LeadManagementApp datasource — shown when concurrent user limit is reached (same shapes as AccessDenied*). */
  AccessDeniedMaxConcurrentTitle?: TextField;
  AccessDeniedMaxConcurrentSummary?: TextField;
  AccessDeniedMaxConcurrentInstructions?: { value: string };
  AccessDeniedMaxConcurrentCTAButton?: {
    value: {
      href?: string;
      text?: string;
    };
  };
  AccessDeniedMaxConcurrentHelpText?: TextField;
}

/** Maps max-concurrent datasource fields into the view shape; missing pieces fall back to generic access denied fields. */
function replaceMaxUsersMergeTag(
  value: string | number | undefined,
  maxUsersCount?: number
): string | undefined {
  if (value == null) return value;
  const textValue = String(value);
  const replacement =
    typeof maxUsersCount === 'number' && Number.isFinite(maxUsersCount)
      ? String(maxUsersCount)
      : '';
  return textValue
    .replace(/<\s*MAX_USERS_COUNT\s*>/gi, replacement)
    .replace(/&lt;\s*MAX_USERS_COUNT\s*&gt;/gi, replacement)
    .replace(/&amp;lt;\s*MAX_USERS_COUNT\s*&amp;gt;/gi, replacement);
}

export function resolveCapacityAccessDeniedFields(
  fields?: AccessDeniedFields,
  maxUsersCount?: number
): AccessDeniedFields | undefined {
  if (!fields) return undefined;
  const title = fields.AccessDeniedMaxConcurrentTitle ?? fields.AccessDeniedTitle;
  const summary = fields.AccessDeniedMaxConcurrentSummary ?? fields.AccessDeniedSummary;
  const instructions =
    fields.AccessDeniedMaxConcurrentInstructions ?? fields.AccessDeniedInstructions;
  const helpText = fields.AccessDeniedMaxConcurrentHelpText ?? fields.AccessDeniedHelpText;

  return {
    AccessDeniedTitle: title
      ? { ...title, value: replaceMaxUsersMergeTag(title.value, maxUsersCount) }
      : undefined,
    AccessDeniedSummary: summary
      ? { ...summary, value: replaceMaxUsersMergeTag(summary.value, maxUsersCount) }
      : undefined,
    AccessDeniedInstructions: instructions
      ? { ...instructions, value: replaceMaxUsersMergeTag(instructions.value, maxUsersCount) || '' }
      : undefined,
    AccessDeniedCTAButton:
      fields.AccessDeniedMaxConcurrentCTAButton ?? fields.AccessDeniedCTAButton,
    AccessDeniedHelpText: helpText
      ? { ...helpText, value: replaceMaxUsersMergeTag(helpText.value, maxUsersCount) }
      : undefined,
  };
}

export function LMAUnauthorizedView({
  fields,
  icon = 'lock',
}: {
  fields?: AccessDeniedFields;
  /** Use `block` for concurrent-user capacity (vs not logged in). */
  icon?: 'lock' | 'block';
}): JSX.Element {
  const handleGoBack = () => {
    const href = fields?.AccessDeniedCTAButton?.value?.href;
    if (href) {
      window.location.href = href;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #255E5E 0%, #1a4a4a 100%)',
        padding: 3,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M50 20 L50 40 M30 40 L70 40 M30 40 L30 60 L70 60 L70 40 M45 40 L45 50 M55 40 L55 50' stroke='rgba(255,255,255,0.15)' stroke-width='2' fill='none'/%3E%3C/svg%3E"),
            url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M40 15 L40 35 M25 35 L55 35 M25 35 L25 55 L55 55 L55 35 M35 35 L35 45 M45 35 L45 45' stroke='rgba(255,255,255,0.1)' stroke-width='2' fill='none'/%3E%3C/svg%3E"),
            repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255, 255, 255, 0.03) 2px, rgba(255, 255, 255, 0.03) 4px),
            repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255, 255, 255, 0.03) 2px, rgba(255, 255, 255, 0.03) 4px)
          `,
          backgroundSize: '100px 100px, 80px 80px, 20px 20px, 20px 20px',
          backgroundPosition: '0 0, 50px 50px, 0 0, 0 0',
          animation: 'lockPatternMove 25s linear infinite',
          pointerEvents: 'none',
          zIndex: 0,
          opacity: 0.6,
        },
        '@keyframes lockPatternMove': {
          '0%': {
            transform: 'translate(0, 0)',
          },
          '100%': {
            transform: 'translate(100px, 100px)',
          },
        },
      }}
    >
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, py: 3 }}>
        <Paper
          elevation={24}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            textAlign: 'center',
            borderRadius: 4,
            backgroundColor: 'white',
            position: 'relative',
            zIndex: 1,
            maxHeight: 'calc(100vh - 48px)',
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: { xs: 100, sm: 120 },
                height: { xs: 100, sm: 120 },
                borderRadius: '50%',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '4px solid #e0e0e0',
                mb: 3,
              }}
            >
              {icon === 'block' ? (
                <BlockIcon
                  sx={{
                    fontSize: { xs: 50, sm: 60 },
                    color: '#255E5E',
                  }}
                />
              ) : (
                <LockIcon
                  sx={{
                    fontSize: { xs: 50, sm: 60 },
                    color: '#255E5E',
                  }}
                />
              )}
            </Box>

            {fields?.AccessDeniedTitle?.value && (
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: '#212121',
                  mb: 1,
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  fontSize: {
                    xs: '1.5rem',
                    sm: '2rem',
                    md: '2.25rem',
                  },
                  lineHeight: 1.2,
                  textAlign: 'center',
                }}
              >
                <Text field={fields.AccessDeniedTitle} />
              </Typography>
            )}

            {fields?.AccessDeniedSummary?.value && (
              <Typography
                variant="body1"
                component="p"
                sx={{
                  color: '#616161',
                  mb: 3,
                  lineHeight: 1.6,
                  textAlign: 'center',
                }}
              >
                <Text field={fields.AccessDeniedSummary} />
              </Typography>
            )}

            <Box
              sx={{
                width: '100%',
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              {fields?.AccessDeniedInstructions?.value && (
                <Box
                  sx={{
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2,
                    p: 2.5,
                    mb: 3,
                    textAlign: 'left',
                    '& h2, & h3, & h4': {
                      color: '#424242',
                      mb: 2,
                      fontWeight: 600,
                      fontSize: '1rem',
                      margin: 0,
                      marginBottom: '16px',
                    },
                    '& ol, & ul': {
                      pl: 3,
                      color: '#616161',
                      margin: 0,
                      '& li': {
                        mb: 1.5,
                        lineHeight: 1.8,
                      },
                    },
                  }}
                >
                  <RichText field={fields.AccessDeniedInstructions} />
                </Box>
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  alignItems: 'center',
                  mt: 3,
                }}
              >
                {fields?.AccessDeniedCTAButton?.value?.href && (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleGoBack}
                    sx={{
                      px: 4,
                      py: 1,
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: 2,
                      backgroundColor: '#255E5E',
                      '&:hover': {
                        backgroundColor: '#1a4a4a',
                      },
                    }}
                  >
                    {fields.AccessDeniedCTAButton.value.text}
                  </Button>
                )}

                {fields?.AccessDeniedHelpText?.value && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#9e9e9e',
                      fontStyle: 'italic',
                      textAlign: 'center',
                    }}
                  >
                    <Text field={fields.AccessDeniedHelpText} />
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
